'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * Human-friendly generation progress: races ahead early, slows down
 * asymptotically as it approaches the end (never parking on one number),
 * never moves backwards, and jumps to 100 the moment the work completes.
 *
 * `floor` is the server-known progress/status milestone — the bar uses it
 * whenever it's ahead of the time curve. `startedAt` (ms epoch) seeds the
 * curve from the real start (e.g. the row's created_at) so a page refresh
 * doesn't reset the bar to zero.
 */
export function useSmoothProgress({
  active,
  done,
  expectedSeconds = 35,
  floor = 0,
  startedAt,
}: {
  active: boolean;
  done: boolean;
  expectedSeconds?: number;
  floor?: number;
  startedAt?: number | null;
}) {
  const [pct, setPct] = useState(0);
  const startRef = useRef<number | null>(null);

  useEffect(() => {
    if (done) {
      setPct(100);
      return;
    }
    if (!active) {
      startRef.current = null;
      setPct(0);
      return;
    }
    if (startRef.current == null) {
      startRef.current = startedAt ?? Date.now();
    }

    const tick = () => {
      const t = (Date.now() - (startRef.current ?? Date.now())) / 1000;
      // Reaches ~63% of the 94-point ceiling at expectedSeconds, then
      // crawls — the classic "slow down at the end".
      const curve = 94 * (1 - Math.exp(-t / (expectedSeconds * 0.9)));
      setPct((p) => Math.min(97, Math.max(p, curve, floor)));
    };
    tick();
    const iv = setInterval(tick, 250);
    return () => clearInterval(iv);
  }, [active, done, expectedSeconds, floor, startedAt]);

  return Math.round(pct);
}
